/**
 * Hook to fetch all themes with their colours from Drupal
 * Shared hook used by ThemeBadges, FilterThemesAutocomplete, and other components
 * Fetches once and caches the result
 */

'use client';

import { useEffect, useState } from 'react';

export interface ThemeWithColor {
  id: string;
  name: string;
  color?: string | null;
  parentId?: string;
  level?: number;
}

/**
 * Hook to fetch all themes from Drupal with their colours
 * Returns a map of theme ID -> full theme data for fast lookup
 * 
 * This hook fetches ALL themes once on mount and caches the result
 * Same approach as useThemeHierarchyEnrichment but with colour map optimisation
 * 
 * @returns Object with themes map, loading state, and error
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { themesMap, loading, error } = useAllThemes();
 *   
 *   const themeColor = themesMap.get(themeId)?.color;
 *   return <div style={{ borderColor: themeColor }}>...</div>;
 * }
 * ```
 */
export function useAllThemes(): {
  /** Map of theme ID -> theme data (includes colour, parentId, etc.) */
  themesMap: Map<string, ThemeWithColor>;
  /** Flat array of all themes */
  themes: ThemeWithColor[];
  /** Whether themes are currently being fetched */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
} {
  const [themesMap, setThemesMap] = useState<Map<string, ThemeWithColor>>(new Map());
  const [themes, setThemes] = useState<ThemeWithColor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchAllThemes() {
      setLoading(true);
      setError(null);

      try {
        // Dynamic import to avoid bundling server-side code
        const { getThemes } = await import('../api/client');

        // Fetch ALL themes from Drupal
        const fetchedThemes = await getThemes();

        if (!cancelled) {
          // Transform to our format
          const themesList: ThemeWithColor[] = fetchedThemes.map(theme => ({
            id: theme.id,
            name: theme.name,
            color: theme.color || null,
            parentId: theme.parentId || undefined,
          }));

          // Build map for fast lookup
          const map = new Map<string, ThemeWithColor>();
          themesList.forEach(theme => {
            map.set(theme.id, theme);
          });

          setThemes(themesList);
          setThemesMap(map);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[useAllThemes] Failed to fetch themes:', err);
          setError(err instanceof Error ? err : new Error('Failed to fetch themes'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchAllThemes();

    return () => {
      cancelled = true;
    };
  }, []); // Only fetch once on mount

  return { themesMap, themes, loading, error };
}

